import { QueryClient, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import {
  APIListQueryResult,
  BaseAPIListQueryOptions,
  findAPIListQueryItem,
  useAPIListQuery,
} from 'shared/api/APIListQuery';
import {
  APIMutationOptions,
  APIMutationResult,
  useAPIMutation,
} from 'shared/api/APIMutation';
import {
  APIQueryOptions,
  APIQueryResult,
  useAPIQuery,
  UseAPIQueryOptions,
} from 'shared/api/APIQuery';
import { APIResponse, requestCarrierAPI } from 'shared/api/CarrierAPIClient';
import { requestCarrierAPIPublic } from 'shared/api/PublicCarrierAPIClient';
import { useInvalidateSubscriptionBillableSeats } from 'shared/modules/subscription/SubscriptionAPI';
import {
  UserDTO,
  UserListParamsDTO,
  userSchema,
  UsersCountDTO,
  usersCountSchema,
  UsersPageFiltersDTO,
  UserInvitationDTO,
  userInvitationSchema,
  UserActivateDTO,
  UserInviteDTO,
  UserDeactivationFormValues,
} from './UsersDTO';

export function mapFiltersToParams(
  filters: UsersPageFiltersDTO,
): UserListParamsDTO {
  return {
    q: !!filters.q && filters.q.length > 2 ? filters.q : undefined,
    status:
      filters.status === 'all'
        ? undefined
        : (filters.status as UserListParamsDTO['status']),
    role_in:
      filters.role === 'all'
        ? undefined
        : (filters.role as UserListParamsDTO['role']),
    ordering:
      filters.ordering === null
        ? undefined
        : (filters.ordering as UserListParamsDTO['ordering']),
    type: filters.type ?? undefined,
  };
}

const USER_LIST_CACHE_KEY = ['users', 'users-list'] as const;

export function useUsers(
  params?: UserListParamsDTO,
  options?: BaseAPIListQueryOptions<UserDTO>,
): APIListQueryResult<UserDTO> {
  return useAPIListQuery(
    [...USER_LIST_CACHE_KEY, { params }],
    (page) => {
      return requestCarrierAPI([
        'GET /internal/web/users/{?page,params*}',
        { params, page },
      ]);
    },
    { ...options, schema: userSchema, keepPreviousData: false, gcTime: 0 },
  );
}

export function useUsersCount(
  params?: UserListParamsDTO,
  options?: APIQueryOptions<UsersCountDTO>,
): APIQueryResult<UsersCountDTO> {
  return useAPIQuery(
    ['users', 'users-count', { params }],
    () => {
      return requestCarrierAPI([
        'GET /internal/web/users/count/{?params*}',
        { params },
      ]);
    },
    { ...options, schema: usersCountSchema },
  );
}

export function useUsersCountCache() {
  const queryClient = useQueryClient();

  const invalidateUsersCount = () => {
    void queryClient.invalidateQueries({ queryKey: ['users', 'users-count'] });
  };

  const updatePendingJoinRequestCount = (counts: number) => {
    void queryClient.setQueryData<UsersCountDTO>(
      ['users', 'users-count', { status: 'pending', type: 'join_request' }],
      (prev) => ({
        driver: prev?.driver ?? 0,
        office_staff: prev?.office_staff ?? 0,
        dispatching: prev?.dispatching ?? 0,
        accounting: prev?.accounting ?? 0,
        operations: prev?.operations ?? 0,
        admin: prev?.admin ?? 0,
        all: counts,
      }),
    );
  };

  return { updatePendingJoinRequestCount, invalidateUsersCount };
}

export const USER_DETAILS_CACHE_KEY = ['users', 'user-details'] as const;
export function useUserDetails(
  guid: string | undefined,
  options?: APIQueryOptions<UserDTO>,
): APIQueryResult<UserDTO> {
  const queryClient = useQueryClient();

  return useAPIQuery(
    [...USER_DETAILS_CACHE_KEY, { guid }],
    () => {
      return requestCarrierAPI(['GET /internal/web/users/{guid}/', { guid }]);
    },
    {
      ...options,
      schema: userSchema,
      enabled: !!guid,
      placeholderData() {
        return findAPIListQueryItem(
          ['users', 'users-list'],
          queryClient,
          (user: UserDTO) => user.guid === guid,
        );
      },
    },
  );
}

export function useUpdateUserInternalNotes() {
  const queryClient = useQueryClient();

  return useAPIMutation<{ guid: string; internalNotes: string }>(
    ({ guid, internalNotes }) => {
      return requestCarrierAPI(
        ['PUT /internal/web/users/{guid}/notes/', { guid }],
        {
          json: { internal_notes: internalNotes },
        },
      ).then((response) => {
        queryClient.setQueryData(
          ['users', 'user-details', { guid }],
          (old: UserDTO) => {
            return { ...old, internal_notes: internalNotes };
          },
        );

        void queryClient.invalidateQueries({
          queryKey: ['users', 'users-list'],
        });

        return response;
      });
    },
  );
}

interface InviteUserParams {
  name: string;
  email: string;
  phone_number: string;
  roles: string[];
}

export function useInviteUserMutation(
  options?: APIMutationOptions<InviteUserParams, UserInviteDTO>,
): APIMutationResult<InviteUserParams, UserInviteDTO> {
  const queryClient = useQueryClient();
  return useAPIMutation((params) => {
    return requestCarrierAPI('POST /internal/web/users/', {
      json: params,
    }).then((response) => {
      void queryClient.invalidateQueries({ queryKey: ['users', 'users-list'] });
      invalidateSeatsCount(queryClient);
      return response as APIResponse<UserInviteDTO>;
    });
  }, options);
}

interface UpdateUserParams {
  guid: string;
  internalName: string;
  roles: string[];
}

export function useUpdateUserMutation(
  options?: APIMutationOptions<UpdateUserParams, UserInviteDTO>,
): APIMutationResult<UpdateUserParams, UserInviteDTO> {
  const { invalidateUserList, invalidateUserDetails } = useUsersCache();

  return useAPIMutation(({ guid, internalName, roles }) => {
    return requestCarrierAPI(['PATCH /internal/web/users/{guid}/', { guid }], {
      json: { internal_name: internalName, roles },
    }).then((response) => {
      invalidateUserList();
      invalidateUserDetails(guid);
      return response as APIResponse<UserInviteDTO>;
    });
  }, options);
}

export function useResendUserMutation(
  options?: APIMutationOptions<{ guid: string }>,
): APIMutationResult<{ guid: string }> {
  return useAPIMutation(({ guid }) => {
    return requestCarrierAPI([
      'POST /internal/web/users/{guid}/resend-invite/',
      { guid },
    ]);
  }, options);
}

export function useCancelUserMutation(
  options?: APIMutationOptions<{ guid: string }>,
): APIMutationResult<{ guid: string }> {
  const queryClient = useQueryClient();

  return useAPIMutation(({ guid }) => {
    return requestCarrierAPI([
      'POST /internal/web/users/{guid}/cancel-invite/',
      { guid },
    ]).then((response) => {
      void queryClient.invalidateQueries({ queryKey: ['users', 'users-list'] });
      invalidateSeatsCount(queryClient);
      return response;
    });
  }, options);
}

interface UserInvitationDetailsParams {
  invitationCode: string;
}

export function useUserInvitationDetails(
  params: UserInvitationDetailsParams,
  options?: UseAPIQueryOptions<UserInvitationDTO>,
): APIQueryResult<UserInvitationDTO> {
  return useAPIQuery(
    ['users', 'invitation-details', { params }],
    () =>
      requestCarrierAPIPublic([
        'GET /internal/web/users/invites/{invitationCode}/',
        params,
      ]),
    { ...options, schema: userInvitationSchema },
  );
}

export type UserAcceptInvitationResponse = APIResponse<UserInvitationDTO> & {
  url: string | null;
};

export function useUsersAPI() {
  const queryClient = useQueryClient();
  const { invalidateUserList, invalidateUserDetails } = useUsersCache();
  const invalidateSubscriptionBillableSeats =
    useInvalidateSubscriptionBillableSeats();
  const { invalidateUsersCount } = useUsersCountCache();

  return useMemo(
    () => ({
      acceptInvite: (invitationCode: string, json: UserActivateDTO) => {
        return requestCarrierAPIPublic<UserAcceptInvitationResponse>(
          [
            'POST /internal/web/users/invites/{invitationCode}/accept/',
            { invitationCode },
          ],
          { json },
        ).then((response) => {
          invalidateQuery(queryClient, invitationCode, 'activated');
          return response;
        });
      },
      acceptJoinRequest: (guid: string) => {
        return requestCarrierAPI<APIResponse<UserDTO>>([
          'POST /internal/web/users/{guid}/accept-join-request/',
          { guid },
        ]);
      },
      declineJoinRequest: (guid: string) => {
        return requestCarrierAPI([
          'POST /internal/web/users/{guid}/decline-join-request/',
          { guid },
        ]).then((response) => {
          invalidateUserList();
          invalidateUserDetails(guid);
          return response;
        });
      },
      acceptExistingUserJoinRequest: (invitationCode: string) => {
        return requestCarrierAPIPublic<UserAcceptInvitationResponse>([
          'POST /internal/web/users/invites/{invitationCode}/accept-existing-user/',
          { invitationCode },
        ]).then((response) => {
          invalidateQuery(queryClient, invitationCode, 'activated');
          void queryClient.invalidateQueries({
            queryKey: ['users', 'invitation-details'],
          });
          return response;
        });
      },
      declineExistingUserJoinRequest: (invitationCode: string) => {
        return requestCarrierAPIPublic([
          'POST /internal/web/users/invites/{invitationCode}/decline/',
          { invitationCode },
        ]).then((response) => {
          invalidateQuery(queryClient, invitationCode, 'declined');
          void queryClient.invalidateQueries({
            queryKey: ['users', 'invitation-details'],
          });
          return response;
        });
      },
      deactivateUser: (
        connectionGUID: string,
        { reason_type, reason }: UserDeactivationFormValues,
      ) => {
        return requestCarrierAPI(
          [
            'POST /internal/web/users/{connectionGUID}/deactivate/',
            { connectionGUID },
          ],
          {
            json:
              reason_type === 'other'
                ? { reason_type, reason }
                : { reason_type },
          },
        ).then((response) => {
          void queryClient.invalidateQueries({
            queryKey: ['users', 'user-details'],
          });
          void queryClient.invalidateQueries({
            queryKey: ['users', 'users-list'],
          });
          invalidateSubscriptionBillableSeats();
          return response;
        });
      },

      reinviteUser: (guid: string) => {
        return requestCarrierAPI([
          'POST /internal/web/users/{guid}/reinvite/',
          { guid },
        ]).then((response) => {
          void queryClient.invalidateQueries({
            queryKey: ['users', 'user-details'],
          });
          void queryClient.invalidateQueries({
            queryKey: ['users', 'users-list'],
          });
          invalidateSeatsCount(queryClient);
          return response;
        });
      },
      removeUser: (guid: string) => {
        return requestCarrierAPI([
          'DELETE /internal/web/users/{guid}/',
          { guid },
        ]).then((response) => {
          void queryClient.invalidateQueries({
            queryKey: ['users', 'user-details'],
          });
          void queryClient.invalidateQueries({
            queryKey: ['users', 'users-list'],
          });
          invalidateUsersCount();
          return response;
        });
      },
      inviteUser: (params: InviteUserParams) => {
        return requestCarrierAPI<APIResponse<UserDTO>>(
          'POST /internal/web/users/',
          {
            json: params,
          },
        ).then((response) => {
          invalidateUserDetails(response.data.guid);
          invalidateUserList();
          invalidateUsersCount();
          return response;
        });
      },
    }),
    [
      queryClient,
      invalidateUserList,
      invalidateUserDetails,
      invalidateSubscriptionBillableSeats,
      invalidateUsersCount,
    ],
  );
}

export function useDeclineExistingUserRequest(
  options?: APIMutationOptions<string>,
) {
  const { declineExistingUserJoinRequest } = useUsersAPI();

  return useAPIMutation(
    (invitationCode) => declineExistingUserJoinRequest(invitationCode),
    options,
  );
}

export function useAcceptExistingUserRequest(
  options?: APIMutationOptions<string>,
) {
  const { acceptExistingUserJoinRequest } = useUsersAPI();

  return useAPIMutation(
    (invitationCode) => acceptExistingUserJoinRequest(invitationCode),
    options,
  );
}

export function useReinviteUser(options?: APIMutationOptions<string>) {
  const { reinviteUser } = useUsersAPI();

  return useAPIMutation((userId) => reinviteUser(userId), options);
}

export function useAcceptJoinRequest(
  options?: APIMutationOptions<string, UserDTO>,
) {
  const { acceptJoinRequest } = useUsersAPI();
  const { invalidateUserList, invalidateUserDetails } = useUsersCache();
  const invalidateSubscriptionBillableSeats =
    useInvalidateSubscriptionBillableSeats();

  return useAPIMutation(
    (guid) =>
      acceptJoinRequest(guid).then((response) => {
        invalidateUserList();
        invalidateUserDetails(guid);
        invalidateSubscriptionBillableSeats();
        return response;
      }),
    options,
  );
}

interface SendDriverCdlVerificationRequestParams {
  userGuid: string;
  driverGuid: string;
}

export function useSendDriverCdlVerificationRequest(
  options?: APIMutationOptions<SendDriverCdlVerificationRequestParams>,
) {
  const { invalidateUserDetails, invalidateUserList } = useUsersCache();

  return useAPIMutation<SendDriverCdlVerificationRequestParams>(
    ({ userGuid, driverGuid }) => {
      return requestCarrierAPI([
        'POST /internal/web/drivers/{driverGuid}/verification-request/',
        { driverGuid },
      ]).then((response) => {
        invalidateUserList();
        invalidateUserDetails(userGuid);
        return response;
      });
    },
    options,
  );
}

export function invalidateQuery(
  queryClient: QueryClient,
  invitationCode: string,
  status: 'declined' | 'activated' | 'expired' = 'activated',
) {
  const queryKey = [
    'users',
    'invitation-details',
    { params: { invitationCode } },
  ];
  const invitaionDetails =
    queryClient.getQueryData<UserInvitationDTO>(queryKey);

  void queryClient.setQueryData(queryKey, { ...invitaionDetails, status });
  void queryClient.invalidateQueries({ queryKey: ['users', 'users-list'] });
}

function invalidateSeatsCount(queryClient: QueryClient) {
  void queryClient.invalidateQueries({ queryKey: ['users', 'users-count'] });
  void queryClient.invalidateQueries({
    queryKey: ['settings', 'subscription-billable-seats'],
  });
}

export function useUsersCache() {
  const queryClient = useQueryClient();

  return useMemo(
    () => ({
      invalidateUserDetails: (guid: string) => {
        void queryClient.invalidateQueries({
          queryKey: ['users', 'user-details', { guid }],
        });
      },
      invalidateUserList: () => {
        void queryClient.invalidateQueries({
          queryKey: ['users', 'users-list'],
        });
      },
    }),
    [queryClient],
  );
}
